import * as React from 'react';
import { useState, useEffect } from 'react';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import { format, parse, parseISO, isValid, startOfDay } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { DEFAULT_TIMEZONE, getDateRangeFromPreset, dateRangeToStrings } from '@/utils/date-filters';

interface DateRange {
  from: Date | undefined;
  to: Date | undefined;
}

// Normalize a date to a local Date object using the same calendar day (Y-M-D)
// This avoids timezone shifts when Date objects are created at UTC midnight.
// Uses UTC methods because getDateRangeFromPreset returns dates at UTC midnight.
const toLocalDateSameDay = (date?: Date) => {
  if (!date) return undefined;
  // Use UTC methods to extract date components from UTC-based dates
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const normalizeRangeToLocal = (range: DateRange): DateRange => ({
  from: toLocalDateSameDay(range.from),
  to: toLocalDateSameDay(range.to),
});

interface DateRangePickerWithApplyProps {
  dateRange: string;
  customDateRange: DateRange;
  onApply: (
    dateRange: string,
    customRange: { from: Date; to: Date },
    startDate: string,
    endDate: string
  ) => void;
  presets?: Array<{ value: string; label: string }>;
  align?: 'start' | 'center' | 'end';
  triggerClassName?: string;
  triggerVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  allowFutureDates?: boolean;
  /** Extra classes for the popover content — e.g. a higher z-index when rendered inside a modal. */
  contentClassName?: string;
  /** ISO date string for the location's first transaction, shown as a subtitle under "All Time". */
  firstTransactionDate?: string;
}

export const DEFAULT_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'thisWeek', label: 'This Week' },
  { value: 'lastWeek', label: 'Last Week' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisYear', label: 'This Year' },
  { value: 'lastYear', label: 'Last Year' },
];

export function DateRangePickerWithApply({
  dateRange,
  customDateRange,
  onApply,
  presets = DEFAULT_PRESETS,
  align = 'start',
  triggerClassName,
  triggerVariant = 'outline',
  allowFutureDates = false,
  contentClassName,
  firstTransactionDate,
}: DateRangePickerWithApplyProps) {
  const [open, setOpen] = useState(false);

  // Pending state - what the user is selecting but hasn't applied yet
  const [pendingDateRange, setPendingDateRange] = useState(dateRange);
  const [pendingCustomRange, setPendingCustomRange] = useState<DateRange>(
    normalizeRangeToLocal(customDateRange)
  );
  const [month, setMonth] = useState<Date | undefined>(
    toLocalDateSameDay(customDateRange.from || customDateRange.to)
  );
  
  // Input field state for manual date entry
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');

  // Reset pending state when popover opens
  useEffect(() => {
    if (open) {
      setPendingDateRange(dateRange);
      const normalized = normalizeRangeToLocal(customDateRange);
      setPendingCustomRange(normalized);
      setMonth(normalized.from || normalized.to);
      // Initialize input fields with current dates
      setStartDateInput(normalized.from ? format(normalized.from, 'MM/dd/yyyy') : '');
      setEndDateInput(normalized.to ? format(normalized.to, 'MM/dd/yyyy') : '');
    }
  }, [open, dateRange, customDateRange]);

  // Keep calendar month in sync with pending selection so presets jump to the right month
  useEffect(() => {
    if (pendingCustomRange.from) {
      setMonth(toLocalDateSameDay(pendingCustomRange.from));
    } else if (pendingCustomRange.to) {
      setMonth(toLocalDateSameDay(pendingCustomRange.to));
    }
  }, [pendingCustomRange.from, pendingCustomRange.to]);
  
  // Keep input fields in sync with calendar/preset selections
  useEffect(() => {
    if (pendingCustomRange.from) {
      setStartDateInput(format(pendingCustomRange.from, 'MM/dd/yyyy'));
    }
    if (pendingCustomRange.to) {
      setEndDateInput(format(pendingCustomRange.to, 'MM/dd/yyyy'));
    }
  }, [pendingCustomRange.from, pendingCustomRange.to]);

  // Get display label for current (applied) state
  const getDisplayLabel = () => {
    const preset = presets.find((p) => p.value === dateRange);
    if (preset) return preset.label;

    if (dateRange === 'all' || dateRange === 'allTime') return 'All Time';

    if (dateRange === 'custom' && customDateRange.from && customDateRange.to) {
      if (customDateRange.from.toDateString() === customDateRange.to.toDateString()) {
        return format(customDateRange.from, 'MMM d, yyyy');
      }
      return `${format(customDateRange.from, 'MMM d')} - ${format(customDateRange.to, 'MMM d, yyyy')}`;
    }
    return 'Select Range';
  };

  // Get subtitle for current (applied) state
  const getDisplaySubtitle = () => {
    if (dateRange === 'today' || dateRange === 'yesterday') {
      const range = getDateRangeFromPreset(dateRange, DEFAULT_TIMEZONE);
      const displayDate = toLocalDateSameDay(range.from);
      return displayDate ? format(displayDate, 'MMM d, yyyy') : '';
    }
    if ((dateRange === 'all' || dateRange === 'allTime') && firstTransactionDate) {
      return `Beginning ${format(parseISO(firstTransactionDate), 'MMM d, yyyy')}`;
    }
    return '';
  };

  // Handle preset selection (immediately applies and closes popover)
  const handlePresetClick = (preset: string) => {
    const range = getDateRangeFromPreset(preset, DEFAULT_TIMEZONE);
    const normalizedFrom = toLocalDateSameDay(range.from);
    const normalizedTo = toLocalDateSameDay(range.to);
    
    if (normalizedFrom && normalizedTo) {
      const strings = dateRangeToStrings({
        from: normalizedFrom,
        to: normalizedTo,
      });
      onApply(preset, { from: normalizedFrom, to: normalizedTo }, strings.startDate, strings.endDate);
      setOpen(false);
    }
  };

  // Handle calendar selection (stages it, doesn't apply)
  // If only 'from' is selected (single click), auto-set 'to' to the same date for single-day selection
  const handleCalendarSelect = (range: any) => {
    if (range?.from) {
      setPendingDateRange('custom');
      // If no 'to' date, set it to the same as 'from' for single-day selection
      setPendingCustomRange({ 
        from: range.from, 
        to: range.to || range.from 
      });
    } else {
      setPendingCustomRange({ from: undefined, to: undefined });
    }
  };
  
  // Handle manual date input changes
  const handleStartDateChange = (value: string) => {
    setStartDateInput(value);
    const parsed = parse(value, 'MM/dd/yyyy', new Date());
    if (isValid(parsed) && value.length === 10) {
      const clamped = !allowFutureDates && parsed > startOfDay(new Date()) ? startOfDay(new Date()) : parsed;
      setPendingDateRange('custom');
      setPendingCustomRange(prev => ({
        from: clamped,
        to: prev.to && clamped <= prev.to ? prev.to : clamped
      }));
    }
  };
  
  const handleEndDateChange = (value: string) => {
    setEndDateInput(value);
    const parsed = parse(value, 'MM/dd/yyyy', new Date());
    if (isValid(parsed) && value.length === 10) {
      const clamped = !allowFutureDates && parsed > startOfDay(new Date()) ? startOfDay(new Date()) : parsed;
      setPendingDateRange('custom');
      setPendingCustomRange(prev => ({
        from: prev.from && clamped >= prev.from ? prev.from : clamped,
        to: clamped
      }));
    }
  };

  // Apply the pending selection
  const handleApply = () => {
    if (pendingCustomRange.from && pendingCustomRange.to) {
      const from = pendingCustomRange.from;
      const to = pendingCustomRange.to;
      
      // Auto-detect known presets: if the user picked a single day that matches
      // today or yesterday, resolve to that preset instead of 'custom'.
      // This ensures the server uses the correct live data path for today,
      // and the client gets proper auto-refresh behavior.
      let resolvedPreset = pendingDateRange;
      if (pendingDateRange === 'custom' && from.toDateString() === to.toDateString()) {
        const todayRange = getDateRangeFromPreset('today', DEFAULT_TIMEZONE);
        const todayLocal = toLocalDateSameDay(todayRange.from);
        if (todayLocal && from.toDateString() === todayLocal.toDateString()) {
          resolvedPreset = 'today';
        } else {
          const yesterdayRange = getDateRangeFromPreset('yesterday', DEFAULT_TIMEZONE);
          const yesterdayLocal = toLocalDateSameDay(yesterdayRange.from);
          if (yesterdayLocal && from.toDateString() === yesterdayLocal.toDateString()) {
            resolvedPreset = 'yesterday';
          }
        }
      }
      
      const strings = dateRangeToStrings({ from, to });
      onApply(resolvedPreset, { from, to }, strings.startDate, strings.endDate);
      setOpen(false);
    }
  };

  // Cancel and close
  const handleCancel = () => {
    setOpen(false);
  };

  // Check if pending selection includes a future date (from manual input)
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  const isFutureDate = !allowFutureDates && (
    (pendingCustomRange.from && pendingCustomRange.from > todayEnd) ||
    (pendingCustomRange.to && pendingCustomRange.to > todayEnd)
  );

  // Check if we have a valid pending selection
  const canApply = pendingCustomRange.from && pendingCustomRange.to && !isFutureDate;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={triggerVariant}
          className={triggerClassName || 'justify-start text-left font-normal'}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          <div className="flex flex-col items-center">
            <span className="font-medium">{getDisplayLabel()}</span>
            {getDisplaySubtitle() && (
              <span className="text-xs text-muted-foreground">{getDisplaySubtitle()}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={`filter-popout-glow !w-auto max-w-fit border border-border bg-popover p-0 text-popover-foreground dark:bg-muted${contentClassName ? ` ${contentClassName}` : ''}`}
        align={align}
      >
        <div className="flex w-fit">
          {/* Calendar */}
          <Calendar
            mode="range"
            selected={{ from: pendingCustomRange.from, to: pendingCustomRange.to }}
            onSelect={handleCalendarSelect}
            month={month}
            onMonthChange={setMonth}
            disabled={allowFutureDates ? undefined : { after: new Date() }}
            toDate={allowFutureDates ? undefined : new Date()}
            initialFocus
          />

          {/* Presets sidebar */}
          <div className="border-l flex flex-col max-w-[225px]">
            <div className="px-2 py-2 flex-1">
              <div className="space-y-0.5">
                {presets.map((preset) => (
                  <button
                    key={preset.value}
                    className={`w-full text-left px-1.5 py-1 rounded text-sm hover:bg-accent whitespace-nowrap ${
                      pendingDateRange === preset.value
                        ? 'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400'
                        : ''
                    }`}
                    onClick={() => handlePresetClick(preset.value)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Date input fields */}
            <div className="px-2 pt-2 pb-1 border-t">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Start date</label>
                  <Input
                    type="text"
                    placeholder="MM/DD/YYYY"
                    value={startDateInput}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    className="h-8 text-sm w-[100px]"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">End date</label>
                  <Input
                    type="text"
                    placeholder="MM/DD/YYYY"
                    value={endDateInput}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    className="h-8 text-sm w-[100px]"
                  />
                </div>
              </div>
            </div>
            
            {isFutureDate && (
              <p className="text-xs text-destructive px-2 pt-1">Future dates are not available</p>
            )}

            {/* Apply/Cancel buttons */}
            <div className="px-2 pb-2 pt-1 flex justify-center gap-2">
              <button
                className="px-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <Button size="sm" className="px-3 text-xs h-7" onClick={handleApply} disabled={!canApply}>
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

